import {ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {first, switchMap} from "rxjs/operators";
import {UserSubscription, UserSubscriptionType} from "../../../../../../user/user-subscription";
import {CountPipe} from "../../../../../pipes/count.pipe";
import {SizePipe} from "../../../../../pipes/size.pipe";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../../store";
import {GoogleAnalyticsService} from "../../../../../services/google-analytics/google-analytics.service";
import {Subject} from "rxjs";
import {Plan} from "../../../one-month-free/models/one-month-free-plan";


export interface ChosenPlan {
  id: number;
  title: string;
  price: number;
  type: 'month' | 'year';
}

const PERCENT_OFFER_END_DATE =  new Date("2022-01-30 18:00:00").getTime();

@Component({
    selector: 'db-booster-pro-select-plan',
    templateUrl: './booster-pro-select-plan.component.html',
    styleUrls: ['./booster-pro-select-plan.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BoosterProSelectPlanComponent implements OnInit, OnDestroy {
  @Output() onPlanChange: EventEmitter<ChosenPlan> = new EventEmitter<ChosenPlan>();

  public chosenPlanIndex: number = 0;
  public chosenPlanIndexCondition: boolean;
  public plans: Plan[];
  public chosenPlanOption: string = 'year';
  public isPercentOffer = false;

  private readonly _destroyStream$ = new Subject<boolean>();

  constructor(
    private count: CountPipe,
    private size: SizePipe,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) { }

  ngOnInit() {
    /*this.chosenPlanIndex = this.isAgency ? 1 : 0;*/
    this.chosenPlanIndexCondition = !!this.chosenPlanIndex;
    this.isPercentOffer = (PERCENT_OFFER_END_DATE - Date.now()) > 1000;
    this.getAvailableSubscriptions();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }


  private getAvailableSubscriptions(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        first(),
        switchMap(() => this._store.select(fromStore.getUserAvailableHostingSubscriptions('speed_booster'))),
        first(res => !!res.length),
      )
      .subscribe(availableSubscriptions => {
        this.formSubscriptions(availableSubscriptions);
        this.changePlan();
      });
  }

  private formSubscriptions(availableSubscriptions): void {
    const categories: string[] = ["personal", "premium", "agency"];
    this.plans = availableSubscriptions.reduce((plans, sbc) => {
      if ((sbc.type !== UserSubscriptionType.PLATFORM && sbc.type !== UserSubscriptionType.BOOSTER) || (sbc.plan && !categories.includes(sbc.plan.title.toLowerCase()))) {
        return plans;
      }
      const plan = plans.find(_plan => _plan.title.toLowerCase() === sbc.plan.title.toLowerCase());
      if (plan) {
        this.updatePlanByPeriod(plan, sbc);
      } else {
        plans = [...plans, this.createPlan(sbc)];
      }
      return plans;
    }, []);
  }

  private createPlan(sbc: UserSubscription): Plan {
    const countPipe = new CountPipe();
    const sizePipe = new SizePipe();
    const plan = new Plan(
      sbc.plan && sbc.plan.title,
      sbc.plan && sbc.plan.services && sbc.plan.services.hosting && sbc.plan.services.hosting.domain_count && sbc.plan.services.hosting.domain_count.parameter_value,
      sizePipe.transform(Number(sbc.plan.services.hosting.hosting_storage.parameter_value * 1024 * 1024)),
      countPipe.transform(parseInt(sbc.plan.services.hosting.hosting_visits.parameter_value, 10))
    );
    return this.updatePlanByPeriod(plan, sbc);
  }

  private updatePlanByPeriod(plan: Plan, sbc: UserSubscription): Plan {
    if (sbc.plan.periodNumber === 1) {
      plan.monthlyId = sbc.id;
      plan.monthlyPrice = sbc.plan.price;
    } else {
      plan.yearlyId = sbc.id;
      plan.yearlyPrice = sbc.plan.price;
    }
    return plan;
  }

  public changePlan() {

    const analyticsData = {
      eventCategory:  'Dashboard Action',
      eventAction: 'Change Subscription (Pro level pop-up)',
      eventLabel: this.plans[this.chosenPlanIndex].title
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    const chosenPlan: ChosenPlan = this.chosenPlanOption === 'month' ? {
      id: this.plans[this.chosenPlanIndex].monthlyId,
      title: this.plans[this.chosenPlanIndex].title,
      price: this.plans[this.chosenPlanIndex].monthlyPrice,
      type: 'month'
    } : {
      id: this.plans[this.chosenPlanIndex].yearlyId,
      title: this.plans[this.chosenPlanIndex].title,
      price: this.plans[this.chosenPlanIndex].yearlyPrice,
      type: 'year'
    };
    this.onPlanChange.emit(chosenPlan);
  }

}
